import { RecurrenceRule } from "@clockwise/client-commons/src/datatypes/RecurrenceRule";
import { Tooltip } from "@clockwise/design-system";
import { AiReschedule } from "@clockwise/icons";
import { appPaths } from "@clockwise/web-commons/src/constants/route.constants";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { AttendeeAvatarStack } from "@clockwise/web-commons/src/ui/AttendeeAvatarStack";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DateTime } from "luxon";
import React from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { PrismButton } from "../../prism/Prism";

type Person = {
  givenName?: null | string;
  familyName?: null | string;
  displayName: string;
  externalImageUrl?: null | string;
};

interface Props {
  eventId: string;
  title: string;
  start: DateTime;
  recurrence?: string | null;
  disruptedAttendees?: Person[];
  organizer?: Person;
  canModify: boolean;
}

export const DisruptiveMeetingRow = ({
  eventId,
  title,
  start,
  recurrence,
  disruptedAttendees,
  organizer,
  canModify,
}: Props) => {
  const navigate = useNavigate();
  const formattedStartDateTime = start.toFormat("EEEE, h:mma");
  const formattedRecurrence = recurrence
    ? new RecurrenceRule(recurrence).toText({
        date: start.toISODate(),
        timezone: getRenderTimeZone(),
      })
    : null;

  const onOptimize = () => {
    const params = createSearchParams({
      id: eventId,
      start: start.toISO(),
      title,
    });
    navigate(`${appPaths.ai}?reschedule&${params.toString()}`, {});
  };

  const ButtonWrapper = ({
    children,
    canModify,
  }: {
    children: React.ReactNode;
    canModify: boolean;
  }) =>
    canModify ? (
      <div>{children}</div>
    ) : (
      <Tooltip
        title={
          <div className="cw-body-sm cw-text-onEmphasis">
            You don't have permission to edit this disruptive meeting. You may need to ask{" "}
            {organizer?.displayName ?? "the organizer"} to optimize this meeting.
          </div>
        }
        arrow
        placement="top"
      >
        <div>{children}</div>
      </Tooltip>
    );

  return (
    <div className="cw-flex cw-justify-between cw-items-center cw-gap-5 cw-py-2 cw-border-default cw-border-solid">
      <div className="cw-space-y-2 cw-grow">
        <div className="cw-body-base cw-font-medium">{title}</div>
        <div className="cw-body-sm cw-text-muted">
          {formattedStartDateTime}
          {formattedRecurrence && ` · ${formattedRecurrence}`}
        </div>
      </div>
      {disruptedAttendees && (
        <div className="cw-flex cw-items-center cw-gap-2">
          <span className="cw-body-sm cw-text-muted">Disruptive for</span>
          <AttendeeAvatarStack size="medium" overlap maxShown={4}>
            {disruptedAttendees.map((attendee, index) => (
              <AttendeeAvatar key={index} profile={attendee} size="medium" />
            ))}
          </AttendeeAvatarStack>
        </div>
      )}
      <ButtonWrapper canModify={canModify}>
        <PrismButton
          variant="outlined"
          size="xsmall"
          endIcon={AiReschedule}
          onClick={onOptimize}
          disabled={!canModify}
        >
          Optimize with Prism
        </PrismButton>
      </ButtonWrapper>
    </div>
  );
};
